// Vector export utilities for print marketing assets.
//
// Two flavors of "true vector" downloads:
//
//   1. downloadAsSvg(node, filename)
//      Serialises a DOM node to an SVG using html-to-image's `toSvg`.
//      The resulting <svg> is resolution-independent — printers can scale
//      to any size without pixelation. Note: the SVG embeds web fonts via
//      <foreignObject>; most modern print RIPs handle this, but for the
//      strictest vector-only workflow use the hand-built PDF below.
//
//   2. buildBusinessCardVectorPdf(opts)
//      A truly hand-coded, primitive-drawn PDF for the Let Go Wellness
//      business card. Uses jsPDF's built-in vector text (Helvetica /
//      Times) and shape primitives — no rasterisation of the type. The
//      logo is embedded as a high-res image (standard for printer logos),
//      everything else (background, text, QR code modules, divider) is
//      pure vector geometry.

import { toSvg } from "html-to-image";
import jsPDF from "jspdf";
import QRCodeLib from "qrcode";

// ─────────────────────────────────────────────────────────────
// SVG download (works for any DOM-rendered marketing asset)
// ─────────────────────────────────────────────────────────────
export async function downloadAsSvg(node, filename) {
  if (!node) return;
  const dataUrl = await toSvg(node, {
    cacheBust: true,
    // Skip CORS-tainted images that would otherwise abort the export.
    filter: () => true,
  });
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename.endsWith(".svg") ? filename : `${filename}.svg`;
  a.click();
}

// Decode the data URL returned by html-to-image into the raw SVG XML string.
// Used by the bulk "printer pack" ZIP so we can drop a real .svg file inside
// instead of a base64 data URI.
export async function nodeToSvgString(node) {
  if (!node) return null;
  const dataUrl = await toSvg(node, { cacheBust: true, filter: () => true });
  // Format is `data:image/svg+xml;charset=utf-8,<urlencoded svg>` OR base64.
  const commaIdx = dataUrl.indexOf(",");
  if (commaIdx < 0) return null;
  const header = dataUrl.slice(0, commaIdx);
  const body = dataUrl.slice(commaIdx + 1);
  if (header.includes(";base64")) {
    // Decode base64
    const binary = atob(body);
    try {
      // Most SVG payloads are utf-8 text
      return decodeURIComponent(escape(binary));
    } catch {
      return binary;
    }
  }
  return decodeURIComponent(body);
}

export async function emailSvgToPrinter(node, baseName, opts = {}) {
  if (!node) return;
  const filename = `${baseName}.svg`;
  await downloadAsSvg(node, filename);
  const subject = encodeURIComponent(opts.subject || `Print order (SVG) — ${baseName}`);
  const body = encodeURIComponent(
    `Hi,\n\nAttached is the print-ready vector artwork:\n\n` +
      `• File: ${filename}\n` +
      `• Format: Scalable Vector Graphic (SVG) — resolution-independent\n` +
      `${opts.notes ? `• Notes: ${opts.notes}\n` : ""}` +
      `\nI've downloaded the SVG to my computer — please confirm receipt once attached.\n\nThank you,\nLet Go Wellness`
  );
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
}

// ─────────────────────────────────────────────────────────────
// Image helper — fetches a URL and returns a JPEG/PNG data URL.
// Lets us embed the logo as a single high-res image inside the
// otherwise fully-vector PDF.
// ─────────────────────────────────────────────────────────────
async function fetchImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Color palette (mirror of Marketing.jsx IMPRINT_* constants)
// ─────────────────────────────────────────────────────────────
const SAGE = [58, 90, 64]; // #3A5A40
const SAGE_DEEP = [36, 59, 44]; // #243B2C
const CREAM = [253, 251, 247]; // #FDFBF7
const GOLD = [201, 162, 75]; // #C9A24B C9A24B
const GOLD_BRIGHT = [232, 198, 105]; // #E8C669

// Set fill colour on a pdf from an [r,g,b] tuple.
function setFill(pdf, [r, g, b]) {
  pdf.setFillColor(r, g, b);
}
function setStroke(pdf, [r, g, b]) {
  pdf.setDrawColor(r, g, b);
}
function setText(pdf, [r, g, b]) {
  pdf.setTextColor(r, g, b);
}

// ─────────────────────────────────────────────────────────────
// Hand-built True Vector PDF — Business Card (3.5" × 2")
// Produces a 2-page PDF: page 1 = front, page 2 = back.
// ─────────────────────────────────────────────────────────────
export async function buildBusinessCardVectorPdf({
  person,
  brand,
  phone,
  phone2,
  email,
  addr1,
  addr2,
  logoUrl,
  domain = "letgowelllness.com",
  domainUrl = "https://letgowelllness.com",
  filename,
  returnBlob = false,
}) {
  const W = 3.5;
  const H = 2;

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "in",
    format: [W, H],
    compress: true,
  });

  // Preload logo once so both pages can embed it without re-fetching.
  const logoDataUrl = logoUrl ? await fetchImageAsDataUrl(logoUrl) : null;

  // ═══════════ FRONT ═══════════
  drawFront(pdf, { brand, person, W, H });

  // ═══════════ BACK ═══════════
  pdf.addPage([W, H], "landscape");
  await drawBack(pdf, {
    brand,
    person,
    phone,
    phone2,
    email,
    addr1,
    addr2,
    logoDataUrl,
    domain,
    domainUrl,
    W,
    H,
  });

  if (returnBlob) {
    return pdf.output("blob");
  }
  pdf.save(filename || `letgo-card-${(person && person.key) || "card"}-vector.pdf`);
  return null;
}

function drawFront(pdf, { brand, person, W, H }) {
  // 1. Solid sage background (closest single colour to the original
  //    sage gradient — printers can simulate the gradient with a tint
  //    if requested in the brief).
  setFill(pdf, SAGE);
  pdf.rect(0, 0, W, H, "F");

  // 2. Subtle inner border line (vector stroke)
  setStroke(pdf, SAGE_DEEP);
  pdf.setLineWidth(0.005);
  pdf.rect(0.08, 0.08, W - 0.16, H - 0.16);

  // 3. Brand name top-left, tiny uppercase, letter-spaced
  setText(pdf, SAGE_DEEP);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.text(spaceLetters((brand || "Let Go Wellness").toUpperCase(), 0.08), 0.2, 0.22);

  // 4. The big "LET GO" wordmark — bold serif, centred horizontally
  setText(pdf, SAGE_DEEP);
  pdf.setFont("times", "bold");
  pdf.setFontSize(48);
  pdf.text("LET  GO", W / 2, H / 2 + 0.06, { align: "center" });

  // 5. The "Wellness" gold script overlay — italic serif, over the
  //    centre, in gold. (jsPDF's standard fonts don't include a true
  //    script face; italic Times approximates the original Sacramento
  //    look while remaining 100% vector.)
  setText(pdf, GOLD);
  pdf.setFont("times", "italic");
  pdf.setFontSize(22);
  pdf.text("Wellness", W / 2, H / 2 + 0.16, { align: "center" });

  // 6. Vertical name + creds along the right edge
  setText(pdf, SAGE_DEEP);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6);
  const right = spaceLetters(
    `${(person?.name || "").toUpperCase()}   ${(person?.creds || "").toUpperCase()}`,
    0.06
  );
  pdf.text(right, W - 0.15, H / 2, { align: "center", angle: 90 });

  // 7. Bottom-left accent dot
  setFill(pdf, SAGE_DEEP);
  pdf.circle(0.22, H - 0.22, 0.035, "F");
}

async function drawBack(pdf, {
  brand,
  person,
  phone,
  phone2,
  email,
  addr1,
  addr2,
  logoDataUrl,
  domain,
  domainUrl,
  W,
  H,
}) {
  // 1. Background
  setFill(pdf, SAGE);
  pdf.rect(0, 0, W, H, "F");

  // Card padding
  const padL = 0.18;
  const padT = 0.15;
  const padB = 0.15;

  // ── LEFT COLUMN — logo + QR ──
  const leftColW = 1.35;
  const logoSize = 0.95;
  const logoX = padL + (leftColW - logoSize) / 2;
  const logoY = padT;

  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoSize, logoSize, undefined, "FAST");
    } catch {
      // ignore – logo embedding is non-fatal
    }
  } else {
    // Fallback monogram circle if the logo can't be embedded
    setFill(pdf, GOLD);
    pdf.circle(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, "F");
    setText(pdf, SAGE_DEEP);
    pdf.setFont("times", "bold");
    pdf.setFontSize(20);
    pdf.text("LG", logoX + logoSize / 2, logoY + logoSize / 2 + 0.07, { align: "center" });
  }

  // QR code – generated as a matrix, drawn as vector squares
  const qrSize = 0.72;
  const qrX = padL + (leftColW - qrSize) / 2;
  const qrY = H - padB - qrSize;

  // White backing plate (small bleed around the modules)
  setFill(pdf, [255, 255, 255]);
  pdf.rect(qrX - 0.02, qrY - 0.02, qrSize + 0.04, qrSize + 0.04, "F");

  await drawQrAsVector(pdf, domainUrl, qrX, qrY, qrSize);

  // ── DIVIDER LINE ──
  const divX = padL + leftColW + 0.04;
  setStroke(pdf, [255, 255, 255]);
  pdf.setLineWidth(0.004);
  pdf.line(divX, padT, divX, H - padB);

  // ── RIGHT COLUMN — contact info ──
  const rx = divX + 0.14;
  let ry = padT + 0.06;

  // Person name (serif, light cream)
  setText(pdf, CREAM);
  pdf.setFont("times", "normal");
  pdf.setFontSize(15);
  pdf.text(person?.name || "", rx, ry);
  ry += 0.16;

  // Title (italic gold)
  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("times", "italic");
  pdf.setFontSize(8);
  pdf.text(person?.title || "", rx, ry);
  ry += 0.11;

  // Creds (uppercase, letter-spaced, faint cream)
  setText(pdf, [220, 215, 200]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(5.5);
  pdf.text(spaceLetters((person?.creds || "").toUpperCase(), 0.08), rx, ry);
  ry += 0.1;

  // Divider hairline under header block
  setStroke(pdf, [255, 255, 255]);
  pdf.setLineWidth(0.003);
  pdf.line(rx, ry, W - 0.18, ry);
  ry += 0.09;

  // Phone (office)
  setText(pdf, [180, 175, 160]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(4.5);
  pdf.text(spaceLetters("OFFICE", 0.08), rx, ry);
  setText(pdf, CREAM);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text(phone || "", rx + 0.26, ry);
  ry += 0.1;

  // Phone (cell) – optional
  if (phone2) {
    setText(pdf, [180, 175, 160]);
    pdf.setFontSize(4.5);
    pdf.text(spaceLetters("CELL", 0.08), rx, ry);
    setText(pdf, CREAM);
    pdf.setFontSize(7);
    pdf.text(phone2, rx + 0.26, ry);
    ry += 0.1;
  }

  // Email
  setText(pdf, CREAM);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text(email || "", rx, ry);
  ry += 0.11;

  // Address
  if (addr1) {
    pdf.setFontSize(6.5);
    pdf.text(addr1, rx, ry);
    ry += 0.09;
  }
  if (addr2) {
    pdf.setFontSize(6.5);
    pdf.text(addr2, rx, ry);
    ry += 0.09;
  }

  // Domain at very bottom-right (uppercase gold)
  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.text(spaceLetters((domain || "").toUpperCase(), 0.1), rx, H - padB - 0.04);
}

// ─────────────────────────────────────────────────────────────
// Hand-built True Vector PDF — Postcard (6" × 4" landscape, 2 pages: front + back)
// ─────────────────────────────────────────────────────────────
export async function buildPostcardVectorPdf({
  brand,
  phone,
  addr1,
  addr2,
  logoUrl,
  domain = "letgowelllness.com",
  domainUrl = "https://letgowelllness.com",
  filename,
  returnBlob = false,
}) {
  const W = 6;
  const H = 4;

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "in",
    format: [W, H],
    compress: true,
  });

  const logoDataUrl = logoUrl ? await fetchImageAsDataUrl(logoUrl) : null;

  // ═══════════ FRONT ═══════════
  // Sage background
  setFill(pdf, SAGE);
  pdf.rect(0, 0, W, H, "F");

  // Inner border line
  setStroke(pdf, [255, 255, 255]);
  pdf.setLineWidth(0.005);
  pdf.rect(0.22, 0.22, W - 0.44, H - 0.44);

  // Centered content block
  const centerX = W / 2;
  let cy = 0.7;

  // Logo (optional)
  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, "PNG", centerX - 0.4, cy, 0.8, 0.8, undefined, "FAST");
      cy += 0.85;
    } catch {
      cy += 0.1;
    }
  } else {
    // Gold monogram circle fallback
    setFill(pdf, GOLD);
    pdf.circle(centerX, cy + 0.4, 0.4, "F");
    setText(pdf, SAGE_DEEP);
    pdf.setFont("times", "bold");
    pdf.setFontSize(18);
    pdf.text("LG", centerX, cy + 0.48, { align: "center" });
    cy += 0.95;
  }

  // "Let Go — Wellness" lockup as vector text
  setText(pdf, CREAM);
  pdf.setFont("times", "normal");
  pdf.setFontSize(18);
  pdf.text("Let Go", centerX - 0.5, cy, { align: "right" });
  setText(pdf, [200, 195, 180]);
  pdf.setFontSize(10);
  pdf.text("—", centerX, cy - 0.02, { align: "center" });
  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("times", "italic");
  pdf.setFontSize(15);
  pdf.text("Wellness", centerX + 0.5, cy, { align: "left" });

  cy += 0.55;

  // Main headline — "You came back for a reason."
  setText(pdf, CREAM);
  pdf.setFont("times", "normal");
  pdf.setFontSize(28);
  pdf.text("You came back for a", centerX, cy, { align: "center" });

  cy += 0.5;
  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("times", "italic");
  pdf.setFontSize(34);
  pdf.text("reason.", centerX, cy, { align: "center" });

  cy += 0.45;
  setText(pdf, GOLD);
  pdf.setFont("times", "italic");
  pdf.setFontSize(11);
  pdf.text("Share us with someone who needs it.", centerX, cy, { align: "center" });

  // ═══════════ BACK ═══════════
  pdf.addPage([W, H], "landscape");

  // Sage background
  setFill(pdf, SAGE);
  pdf.rect(0, 0, W, H, "F");

  // Center divider
  setStroke(pdf, [255, 255, 255]);
  pdf.setLineWidth(0.004);
  pdf.line(W / 2, 0.25, W / 2, H - 0.25);

  // ── LEFT half (message + QR + contact) ──
  const lx = 0.3;
  let ly = 0.5;

  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.text(spaceLetters(`FROM ${(brand || "").toUpperCase()}`, 0.28), lx, ly);
  ly += 0.3;

  setText(pdf, GOLD_BRIGHT);
  pdf.setFont("times", "italic");
  pdf.setFontSize(11);
  const msg = pdf.splitTextToSize(
    "Thank you for trusting us with someone you love. Drop this card to anyone who could use a quiet, modern place to begin.",
    2.5
  );
  pdf.text(msg, lx, ly);
  ly += 0.18 * msg.length;

  // Bottom block: QR + contact lines
  const qrSize = 0.95;
  const qrX = lx;
  const qrY = H - 0.3 - qrSize;

  // White backing
  setFill(pdf, [255, 255, 255]);
  pdf.rect(qrX - 0.04, qrY - 0.04, qrSize + 0.08, qrSize + 0.08, "F");
  setStroke(pdf, [255, 255, 255]);
  pdf.setLineWidth(0.005);
  pdf.rect(qrX - 0.04, qrY - 0.04, qrSize + 0.08, qrSize + 0.08);

  await drawQrAsVector(pdf, domainUrl, qrX, qrY, qrSize);

  // Contact lines next to QR
  const cx = qrX + qrSize + 0.18;
  let ccy = qrY + 0.12;
  setText(pdf, CREAM);
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);
  pdf.text(domain || "", cx, ccy);
  ccy += 0.18;

  setText(pdf, [220, 215, 200]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text(phone || "", cx, ccy);
  ccy += 0.13;
  if (addr1) {
    pdf.text(addr1, cx, ccy);
    ccy += 0.13;
  }
  if (addr2) {
    pdf.text(addr2, cx, ccy);
  }

  // ── RIGHT half (stamp box + address lines) ──
  const rx = W / 2 + 0.3;

  // "Place stamp here" box top-right
  const stampW = 0.85;
  const stampH = 0.95;
  const stampX = W - 0.3 - stampW;
  const stampY = 0.35;
  setStroke(pdf, [220, 215, 200]);
  pdf.setLineWidth(0.004);
  pdf.rect(stampX, stampY, stampW, stampH);
  setText(pdf, [220, 215, 200]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(5);
  pdf.text(spaceLetters("PLACE", 0.22), stampX + stampW / 2, stampY + 0.32, { align: "center" });
  pdf.text(spaceLetters("STAMP", 0.22), stampX + stampW / 2, stampY + 0.5, { align: "center" });
  pdf.text(spaceLetters("HERE", 0.22), stampX + stampW / 2, stampY + 0.68, { align: "center" });

  // Address lines (4 hairlines at bottom of right half)
  setStroke(pdf, [220, 215, 200]);
  pdf.setLineWidth(0.003);
  const lineY0 = H - 1.55;
  const lineGap = 0.32;
  for (let i = 0; i < 4; i += 1) {
    const y = lineY0 + i * lineGap;
    pdf.line(rx, y, W - 0.3, y);
  }

  if (returnBlob) {
    return pdf.output("blob");
  }
  pdf.save(filename || "letgo-postcard-vector.pdf");
  return null;
}

// ─────────────────────────────────────────────────────────────
// Hand-built True Vector PDF — Flyer (US Letter 8.5" × 11")
// Mirrors the design of <ThemedFlyer/> using only jsPDF primitives.
// Single-page portrait. All text is true vector; QR code is drawn as
// vector squares; logo is embedded as image only (industry standard).
// ─────────────────────────────────────────────────────────────
export async function buildFlyerVectorPdf({
  brand,
  phone,
  email,
  addr1,
  addr2,
  services = [],
  logoUrl,
  hoursWd = "Mon–Fri · 8am — 8pm",
  hoursWe = "Sat · 9am — 4pm",
  domain = "letgowelllness.com",
  domainUrl = "https://letgowelllness.com",
  filename,
  returnBlob = false,
}) {
  const W = 8.5;
  const H = 11;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "in",
    format: "letter",
    compress: true,
  });

  // Page margins (match the React design)
  const marginX = 0.85;

  // ── 1. Cream background (single solid fill) ──
  setFill(pdf, CREAM);
  pdf.rect(0, 0, W, H, "F");

  // ── 2. Top band: brand wordmark + domain ──
  const topY = 0.9;
  setText(pdf, SAGE_DEEP);
  pdf.setFont("times", "normal");
  pdf.setFontSize(28);
  pdf.text("Let Go", marginX, topY);
  // Em-dash separator
  setText(pdf, [120, 120, 110]);
  pdf.setFontSize(14);
  pdf.text("—", marginX + 1.3, topY - 0.04);
  // Wellness in italic gold
  setText(pdf, GOLD);
  pdf.setFont("times", "italic");
  pdf.setFontSize(20);
  pdf.text("Wellness", marginX + 1.55, topY);
  // Domain right-aligned
  setText(pdf, [120, 120, 110]);
  pdf.setFont("times", "italic");
  pdf.setFontSize(11);
  pdf.text(domain, W - marginX, topY, { align: "right" });

  // Optional logo embedding at top-right (only if successfully fetched)
  if (logoUrl) {
    try {
      const logoData = await fetchImageAsDataUrl(logoUrl);
      if (logoData) {
        pdf.addImage(logoData, "PNG", W - marginX - 0.85, topY - 0.55, 0.5, 0.5, undefined, "FAST");
      }
    } catch {
      // Non-fatal
    }
  }

  // ── 3. Horizontal divider under top band ──
  const dividerY = topY + 0.3;
  setStroke(pdf, [180, 170, 150]);
  pdf.setLineWidth(0.006);
  pdf.line(marginX, dividerY, W - marginX, dividerY);

  // ── 4. Hero block ──
  let cursor = dividerY + 0.55;
  setText(pdf, [159, 92, 69]); // #9F5C45 overline
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.5);
  pdf.text(spaceLetters("NEW PATIENTS WELCOME · IN-PERSON & TELEHEALTH", 0.08), marginX, cursor);

  cursor += 0.42;
  setText(pdf, [28, 25, 23]);
  pdf.setFont("times", "normal");
  pdf.setFontSize(48);
  pdf.text("You don't have to", marginX, cursor);

  cursor += 0.55;
  pdf.setFont("times", "normal");
  pdf.text("hold it ", marginX, cursor);
  // measure "hold it " so we can place "anymore." after
  setText(pdf, [58, 90, 64]); // sage accent for the italic word
  pdf.setFont("times", "italic");
  pdf.text("anymore.", marginX + 2.0, cursor);

  cursor += 0.45;
  setText(pdf, [87, 83, 78]);
  pdf.setFont("times", "normal");
  pdf.setFontSize(13);
  const heroLines = pdf.splitTextToSize(
    "A modern mental-health clinic in Waterbury, CT — built for the people who carry too much. Anxiety, depression, trauma, burnout, couples & family. We meet you where you are.",
    5.4
  );
  pdf.text(heroLines, marginX, cursor);
  cursor += 0.22 * heroLines.length;

  // ── 5. Services + QR row ──
  cursor += 0.45;
  const colLeftX = marginX;
  const colRightX = W - marginX - 1.9;

  // Section header
  setText(pdf, [159, 92, 69]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.text(spaceLetters("WHAT WE TREAT", 0.08), colLeftX, cursor);

  // Two-column services grid
  const servicesShown = services.slice(0, 6);
  const gridStartY = cursor + 0.25;
  const cellW = 2.3;
  const cellH = 0.85;
  servicesShown.forEach((s, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = colLeftX + col * (cellW + 0.4);
    const y = gridStartY + row * cellH;

    // Square accent bullet
    setFill(pdf, GOLD);
    pdf.rect(x, y - 0.05, 0.08, 0.08, "F");

    // Service name
    setText(pdf, [28, 25, 23]);
    pdf.setFont("times", "normal");
    pdf.setFontSize(14);
    pdf.text(s.name || "", x + 0.18, y + 0.04);

    // Blurb (wrap to column width)
    setText(pdf, [120, 115, 105]);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    const blurbLines = pdf.splitTextToSize(s.blurb || "", cellW - 0.18);
    pdf.text(blurbLines.slice(0, 3), x + 0.18, y + 0.22);

    // Hairline under each item
    setStroke(pdf, [220, 210, 195]);
    pdf.setLineWidth(0.003);
    pdf.line(x, y + cellH - 0.1, x + cellW, y + cellH - 0.1);
  });

  // QR code column (right) - vector squares
  const qrSize = 1.7;
  const qrX = colRightX;
  const qrY = gridStartY - 0.05;

  // White backing card
  setFill(pdf, [255, 255, 255]);
  pdf.rect(qrX - 0.1, qrY - 0.1, qrSize + 0.2, qrSize + 0.2, "F");
  setStroke(pdf, [220, 210, 195]);
  pdf.setLineWidth(0.005);
  pdf.rect(qrX - 0.1, qrY - 0.1, qrSize + 0.2, qrSize + 0.2);

  await drawQrAsVector(pdf, domainUrl, qrX, qrY, qrSize);

  // Scan to enroll
  setText(pdf, [28, 25, 23]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.5);
  pdf.text(spaceLetters("SCAN TO ENROLL", 0.08), qrX + qrSize / 2, qrY + qrSize + 0.3, { align: "center" });

  setText(pdf, GOLD);
  pdf.setFont("times", "italic");
  pdf.setFontSize(13);
  pdf.text(domain, qrX + qrSize / 2, qrY + qrSize + 0.5, { align: "center" });

  // ── 6. Footer (three columns at bottom) ──
  const footerY = H - 0.95;
  setStroke(pdf, [180, 170, 150]);
  pdf.setLineWidth(0.006);
  pdf.line(marginX, footerY - 0.25, W - marginX, footerY - 0.25);

  const colW = (W - 2 * marginX) / 3;

  // VISIT
  setText(pdf, [159, 92, 69]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.text(spaceLetters("VISIT", 0.1), marginX, footerY);
  setText(pdf, [28, 25, 23]);
  pdf.setFont("times", "normal");
  pdf.setFontSize(12);
  pdf.text(addr1 || "", marginX, footerY + 0.2);
  setText(pdf, [120, 115, 105]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(addr2 || "", marginX, footerY + 0.36);

  // CALL (center column)
  const callX = marginX + colW + colW / 2;
  setText(pdf, [159, 92, 69]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.text(spaceLetters("CALL", 0.1), callX, footerY, { align: "center" });
  setText(pdf, [28, 25, 23]);
  pdf.setFont("times", "normal");
  pdf.setFontSize(12);
  pdf.text(phone || "", callX, footerY + 0.2, { align: "center" });
  setText(pdf, [120, 115, 105]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(email || "", callX, footerY + 0.36, { align: "center" });

  // HOURS (right column)
  const hoursX = W - marginX;
  setText(pdf, [159, 92, 69]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.text(spaceLetters("HOURS", 0.1), hoursX, footerY, { align: "right" });
  setText(pdf, [28, 25, 23]);
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);
  pdf.text(hoursWd || "", hoursX, footerY + 0.2, { align: "right" });
  setText(pdf, [120, 115, 105]);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(hoursWe || "", hoursX, footerY + 0.36, { align: "right" });

  if (returnBlob) {
    return pdf.output("blob");
  }
  pdf.save(filename || "letgo-flyer-vector.pdf");
  return null;
}

// ─────────────────────────────────────────────────────────────
// QR code → vector squares
// ─────────────────────────────────────────────────────────────
async function drawQrAsVector(pdf, value, x, y, size) {
  const qr = QRCodeLib.create(value, { errorCorrectionLevel: "H" });
  const modules = qr.modules;
  const N = modules.size;
  const cell = size / N;

  setFill(pdf, [28, 25, 23]); // #1C1917 (matches QRCode.jsx `dark`)
  for (let row = 0; row < N; row += 1) {
    for (let col = 0; col < N; col += 1) {
      if (modules.get(row, col)) {
        pdf.rect(x + col * cell, y + row * cell, cell, cell, "F");
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────
// jsPDF doesn't expose a `letter-spacing` API, so fake it by
// inserting a custom number of spaces between every glyph.
// 0.08–0.10 tracking gives the right luxurious feel at the tiny
// sizes we use for ALL-CAPS micro-typography.
// ─────────────────────────────────────────────────────────────
function spaceLetters(str, _tracking = 0.08) {
  if (!str) return "";
  return str.split("").join(" ");
}
